import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthSearchService, GrowthAeoAnswer } from './growth-search.service';
import { GrowthSeoProfileSelectorComponent } from '../../shared/design-system/growth-seo-profile-selector.component';

interface CitationRow {
  reference: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-aeo-answers',
  imports: [AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent, GrowthSeoProfileSelectorComponent, FormsModule, TableModule, ButtonModule, DialogModule, InputTextModule, TextareaModule],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.search.aeo.title')" [description]="t('growth.search.aeo.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.search.aeo.hint.title'" [descriptionKey]="'growth.search.aeo.hint.description'" [bulletKeys]="['growth.search.aeo.hint.bullets.grounded', 'growth.search.aeo.hint.bullets.approval']" />
      <section class="card">
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.search.aeo.actions.newAnswer')" (click)="openDialog()"></button>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
        </div>
        @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
          <p-table [value]="answers()" [rows]="10" [paginator]="true">
            <ng-template pTemplate="header"><tr><th>Question</th><th>Locale</th><th>Intent</th><th>Approved</th><th>Actions</th></tr></ng-template>
            <ng-template pTemplate="body" let-row><tr><td>{{ row.question }}</td><td>{{ row.locale }}</td><td>{{ row.intentKey }}</td><td>{{ row.approvedAtUtc ? t('common.yes') : t('common.no') }}</td><td class="actions"><button pButton type="button" size="small" severity="secondary" [label]="t('common.edit')" (click)="edit(row)"></button><button pButton type="button" size="small" [label]="t('growth.search.aeo.actions.approve')" [disabled]="!!row.approvedAtUtc" (click)="approve(row)"></button></td></tr></ng-template>
          </p-table>
        }
      </section>
      <p-dialog [header]="t('growth.search.aeo.dialog.title')" [visible]="dialogVisible()" (visibleChange)="dialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(820px,95vw)' }">
        <div class="grid">
          <ain-form-field [label]="t('growth.search.aeo.fields.question')"><input pInputText [(ngModel)]="model.question" /></ain-form-field>
          <ain-form-field [label]="t('growth.search.aeo.fields.locale')"><input pInputText [(ngModel)]="model.locale" /></ain-form-field>
          <ain-form-field [label]="t('growth.search.aeo.fields.intentKey')"><input pInputText [(ngModel)]="model.intentKey" /></ain-form-field>
          <ain-form-field [label]="t('growth.search.aeo.fields.seoProfile')"><ain-growth-seo-profile-selector [(ngModel)]="model.seoProfileId" /></ain-form-field>
          <ain-form-field [label]="t('growth.search.aeo.fields.answerMarkdown')"><textarea pInputTextarea rows="5" [(ngModel)]="model.answerMarkdown"></textarea></ain-form-field>
          <ain-form-field [label]="t('growth.search.aeo.fields.faqPageType')"><input pInputText [(ngModel)]="model.faqPageType" /></ain-form-field>
          <ain-form-field [label]="t('growth.search.aeo.fields.faqQuestionType')"><input pInputText [(ngModel)]="model.faqQuestionType" /></ain-form-field>
          <ain-form-field [label]="t('growth.search.aeo.fields.faqAnswerType')"><input pInputText [(ngModel)]="model.faqAnswerType" /></ain-form-field>
        </div>
        <div class="section-title"><h4>{{ t('growth.search.aeo.sections.citations') }}</h4></div>
        @for (row of citationRows; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.search.aeo.fields.citationRef')"><input pInputText [(ngModel)]="row.reference" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeCitation($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addCitation()"></button>
        <ng-template pTemplate="footer"><button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogVisible.set(false)"></button><button pButton type="button" [label]="t('common.save')" (click)="save()"></button></ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`.card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; } .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit,minmax(220px,1fr)); } .toolbar,.actions { display: flex; gap: 8px; flex-wrap: wrap; } .section-title { margin-top: 10px; } .row-grid { display: grid; gap: 8px; grid-template-columns: minmax(220px,1fr) auto; align-items: end; margin-bottom: 8px; }`]
})
export class GrowthAeoAnswersComponent implements OnInit {
  private readonly service = inject(GrowthSearchService);
  private readonly i18n = inject(LocalizationService);
  readonly loading = signal(false);
  readonly answers = signal<GrowthAeoAnswer[]>([]);
  readonly dialogVisible = signal(false);
  selectedId: string | null = null;
  model = {
    question: '',
    answerMarkdown: '',
    locale: 'en-US',
    intentKey: 'general',
    seoProfileId: null as string | null,
    faqPageType: 'FAQPage',
    faqQuestionType: 'Question',
    faqAnswerType: 'Answer'
  };
  citationRows: CitationRow[] = [{ reference: '' }];
  ngOnInit(): void { this.load(); }
  load(): void { this.loading.set(true); this.service.listAeoAnswers().subscribe({ next: rows => { this.answers.set(rows); this.loading.set(false); }, error: () => this.loading.set(false) }); }
  openDialog(): void {
    this.selectedId = null;
    this.model = { question: '', answerMarkdown: '', locale: 'en-US', intentKey: 'general', seoProfileId: null, faqPageType: 'FAQPage', faqQuestionType: 'Question', faqAnswerType: 'Answer' };
    this.citationRows = [{ reference: '' }];
    this.dialogVisible.set(true);
  }
  edit(row: GrowthAeoAnswer): void {
    this.selectedId = row.id;
    this.model = {
      question: row.question,
      answerMarkdown: row.answerMarkdown,
      locale: row.locale,
      intentKey: row.intentKey,
      seoProfileId: null,
      faqPageType: 'FAQPage',
      faqQuestionType: 'Question',
      faqAnswerType: 'Answer'
    };
    this.citationRows = this.parseCitations(row.citationRefsJson).map(reference => ({ reference }));
    if (this.citationRows.length === 0)
      this.citationRows = [{ reference: '' }];
    this.dialogVisible.set(true);
  }
  save(): void {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': this.model.faqPageType || 'FAQPage',
      mainEntity: [
        {
          '@type': this.model.faqQuestionType || 'Question',
          name: this.model.question,
          acceptedAnswer: {
            '@type': this.model.faqAnswerType || 'Answer',
            text: this.model.answerMarkdown
          }
        }
      ]
    };
    this.service.upsertAeoAnswer({
      id: this.selectedId,
      question: this.model.question,
      answerMarkdown: this.model.answerMarkdown,
      locale: this.model.locale,
      intentKey: this.model.intentKey,
      seoProfileId: this.model.seoProfileId,
      targetContentAssetId: null,
      faqSchemaJson: JSON.stringify(faqSchema),
      citationRefsJson: JSON.stringify(this.citationRows.map(row => row.reference.trim()).filter(Boolean))
    }).subscribe({ next: () => { this.dialogVisible.set(false); this.load(); } });
  }
  addCitation(): void { this.citationRows.push({ reference: '' }); }
  removeCitation(index: number): void { this.citationRows.splice(index, 1); }
  private parseCitations(json: string): string[] {
    try {
      const parsed = JSON.parse(json) as unknown;
      if (!Array.isArray(parsed))
        return [];
      return parsed.map(item => {
        if (typeof item === 'string')
          return item;
        if (item && typeof item === 'object') {
          const candidate = item as Record<string, unknown>;
          return String(candidate['source'] ?? candidate['reference'] ?? candidate['url'] ?? '');
        }
        return '';
      }).filter(Boolean);
    } catch {
      return [];
    }
  }
  approve(row: GrowthAeoAnswer): void { this.service.approveAeoAnswer(row.id).subscribe({ next: () => this.load() }); }
  t(key: string): string { return this.i18n.translate(key); }
}
